import axios from "axios";
import { API_URL } from "../constants";
import {
  Post,
  PostComment,
  PostAcknowledgements,
  PostEvent,
  PostEventInput,
  PostPin,
  PinRequest,
  EventResponse,
  FetchPostsResponse,
  Reaction,
  ReactionPerson,
  ReactionSummary,
  NewsContext,
  NewsAbout,
  NewsSidebar,
} from "types/Post";

/** Feed filter meaning "this company plus everything announced to the group". */
export const WHOLE_GROUP_FILTER = "group";

// What the viewer may do in this company's feed, and the group to filter by
export const fetchNewsContext = async (
  companyId: string
): Promise<NewsContext> => {
  const response = await axios.get(`${API_URL}/${companyId}/news/context`);
  return response.data;
};

// "About" page of a company the viewer only visits (group visitor)
export const fetchNewsAbout = async (companyId: string): Promise<NewsAbout> => {
  const response = await axios.get(`${API_URL}/${companyId}/news/about`);
  return response.data;
};

// Sidebar of the news page (absent today, events, new colleagues, work anniversaries, birthdays) for the same filter as the feed
export const fetchNewsSidebar = async (
  companyId: string,
  filter: string = WHOLE_GROUP_FILTER
): Promise<NewsSidebar> => {
  const response = await axios.get(`${API_URL}/${companyId}/news/sidebar`, {
    params: { filter },
  });
  // A backend that is older than this page leaves out the blocks it does not know yet.
  return {
    events: { total: 0, items: [] },
    birthdays: { total: 0, todayCount: 0, people: [] },
    ...response.data,
  };
};

// Fetch posts with pagination. filter: WHOLE_GROUP_FILTER or the id of one group company
export const fetchPosts = async (
  companyId: string,
  page: number = 1,
  filter: string = WHOLE_GROUP_FILTER
): Promise<FetchPostsResponse> => {
  const url = `${API_URL}/${companyId}/posts/fetch`;
  const response = await axios.post(url, { page, filter });
  // A backend that is older than the pins leaves the pinned section out.
  return { pinnedPosts: [], ...response.data };
};

/** What the composer sends for a new post; `details` null = no long text. */
export interface CreatePostInput {
  companyId: string;
  content: string;
  details: string | null;
  isImportant: boolean;
  fileIds?: string[];
  isGroupPost?: boolean;
  event?: PostEventInput;
}

// The company (and the post) are in the URL; the rest of the input is the request body.
type PostBody = Omit<CreatePostInput, "companyId">;

// Create a new post
export const createPost = async ({
  companyId,
  content,
  details,
  isImportant,
  fileIds,
  isGroupPost,
  event,
}: CreatePostInput): Promise<Post> => {
  const url = `${API_URL}/${companyId}/posts`;
  const body: PostBody = { content, details, isImportant };
  if (fileIds && fileIds.length > 0) {
    body.fileIds = fileIds;
  }
  if (isGroupPost !== undefined) {
    body.isGroupPost = isGroupPost;
  }
  if (event) {
    body.event = event;
  }
  const response = await axios.post(url, body);
  return response.data;
};

// Get single post with comments
export const getPostById = async (
  companyId: string,
  postId: string
): Promise<Post> => {
  const url = `${API_URL}/${companyId}/posts/${postId}`;
  const response = await axios.get(url);
  return response.data;
};

/** What the edit form sends; `details` null takes the long text away. */
export interface UpdatePostInput extends CreatePostInput {
  postId: string;
}

// Update post
export const updatePost = async ({
  companyId,
  postId,
  content,
  details,
  isImportant,
  fileIds,
  isGroupPost,
  event,
}: UpdatePostInput): Promise<Post> => {
  const url = `${API_URL}/${companyId}/posts/${postId}`;
  const body: PostBody = { content, details, isImportant };
  if (fileIds !== undefined) {
    body.fileIds = fileIds;
  }
  if (isGroupPost !== undefined) {
    body.isGroupPost = isGroupPost;
  }
  if (event) {
    body.event = event;
  }
  const response = await axios.patch(url, body);
  return response.data;
};

// Delete post
export const deletePost = async (
  companyId: string,
  postId: string
): Promise<void> => {
  const url = `${API_URL}/${companyId}/posts/${postId}`;
  await axios.delete(url);
};

// Fetch comments for a post
export const fetchComments = async (
  companyId: string,
  postId: string
): Promise<PostComment[]> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/comments`;
  const response = await axios.get(url);
  const data = response.data;
  
  // Handle different response formats
  if (Array.isArray(data)) {
    return data;
  } else if (data && Array.isArray(data.comments)) {
    return data.comments;
  } else if (data && Array.isArray(data.commentsCollection)) {
    return data.commentsCollection;
  }
  
  return [];
};

// Add comment
export const addComment = async (
  companyId: string,
  postId: string,
  comment: string,
  parent?: string
): Promise<PostComment> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/comments`;
  const body: { comment: string; parent?: string } = { comment };
  if (parent) {
    body.parent = parent;
  }
  const response = await axios.post(url, body);
  return response.data;
};

// Update comment
export const updateComment = async (
  companyId: string,
  postId: string,
  commentId: string,
  comment: string
): Promise<PostComment> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/comments/${commentId}`;
  const response = await axios.patch(url, { comment });
  return response.data;
};

// Delete comment
export const deleteComment = async (
  companyId: string,
  postId: string,
  commentId: string
): Promise<void> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/comments/${commentId}`;
  await axios.delete(url);
};

// Give the reaction to a post, or take it back if the viewer gave it already; their other reactions stay
export const toggleReaction = async (
  companyId: string,
  postId: string,
  reaction: Reaction
): Promise<ReactionSummary[]> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/reactions`;
  const response = await axios.post(url, { reaction });
  return response.data.reactions;
};

// The same for a comment or a reply
export const toggleCommentReaction = async (
  companyId: string,
  postId: string,
  commentId: string,
  reaction: Reaction
): Promise<ReactionSummary[]> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/comments/${commentId}/reactions`;
  const response = await axios.post(url, { reaction });
  return response.data.reactions;
};

// Answer "going" / "not going" to the event of a post; null takes the answer back
export const setEventResponse = async (
  companyId: string,
  postId: string,
  response: EventResponse | null
): Promise<{ event: Pick<PostEvent, "goingCount" | "myResponse"> }> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/event-response`;
  const result = await axios.put(url, { response });
  return result.data;
};

// Pin a post to the top of the feed (optionally asking everybody to acknowledge it) or take the pin off
export const pinPost = async (
  companyId: string,
  postId: string,
  request: PinRequest
): Promise<Post> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/pin`;
  const response = await axios.put(url, request);
  return response.data;
};

// The viewer presses "Susipažinau" on a pinned post; repeating it changes nothing
export const acknowledgePost = async (
  companyId: string,
  postId: string
): Promise<{ pin: PostPin }> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/acknowledge`;
  const response = await axios.put(url);
  return response.data;
};

// Who has acknowledged a pinned post and who has not (the post's author and organisers only)
export const fetchAcknowledgements = async (
  companyId: string,
  postId: string
): Promise<PostAcknowledgements> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/acknowledgements`;
  const response = await axios.get(url);
  return response.data;
};

// Who reacted to a post and with what, one entry per person, sorted by name
export const fetchReactionPeople = async (
  companyId: string,
  postId: string
): Promise<ReactionPerson[]> => {
  const url = `${API_URL}/${companyId}/posts/${postId}/reactions`;
  const response = await axios.get(url);
  return response.data.people;
};

// Get file download URL
// Note: Caller is responsible for revoking the blob URL when done using URL.revokeObjectURL()
export const getFileUrl = async (
  companyId: string,
  fileId: string
): Promise<string> => {
  const url = `${API_URL}/${companyId}/group-posts/files/${fileId}?mode=view`;
  const response = await axios.get(url, { responseType: "blob" });
  // response.data is already a Blob, no need to wrap it
  const objectUrl = URL.createObjectURL(response.data);
  return objectUrl;
};

